import Button, { type ButtonProps } from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuList from '@mui/material/MenuList'
import { type SxProps, styled, type Theme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import { isEmpty } from 'ramda'
import React from 'react'

import MenuItem from '@/app/component/atom/menu-item'
import { PersonalizationType, PzGroup, SinglePersonalizationType } from '@/app/types'

import { getGroupTitle } from './pz-metadata'
import CustomTooltip from './tooltip'

type Props = {
  color?: ButtonProps['color']
  containerSx?: SxProps<Theme>
  disabled?: boolean
  id: string
  personalizationList: PersonalizationType
  testId?: {
    button: string
    menu: string
  }
  variant?: ButtonProps['variant']

  onClose?: () => void
  onOpen?: () => void
  onSelect: (pz: SinglePersonalizationType) => void
}

export type PzMenuRef = {
  close: () => void
  open: () => void
}

// Lists and filters available personalizations, then passes the selected field to its caller for insertion.
const PzMenu = React.forwardRef<PzMenuRef, Props>(
  ({ color, containerSx, disabled, id, personalizationList, testId, variant, onClose, onOpen, onSelect }, ref) => {
    const [pzOpen, setPzOpen] = React.useState(false)
    const [search, setSearch] = React.useState('')
    const anchorEl = React.useRef<HTMLDivElement | null>(null)
    const searchInputRef = React.useRef<HTMLInputElement | null>(null)

    const closeMenu = React.useCallback(() => {
      setPzOpen(false)
      setSearch('')
    }, [])
    const openMenu = React.useCallback(() => {
      setPzOpen(true)
    }, [])

    React.useImperativeHandle(
      ref,
      () => ({
        close: closeMenu,
        open: openMenu,
      }),
      [closeMenu, openMenu],
    )

    const onMenuOpen = React.useCallback(() => {
      if (disabled) {
        return
      }
      openMenu()
      onOpen?.()
    }, [disabled, onOpen, openMenu])
    const onMenuClose = React.useCallback(() => {
      closeMenu()
      onClose?.()
    }, [closeMenu, onClose])
    const onMenuEntered = React.useCallback(() => {
      searchInputRef.current?.focus({ preventScroll: true })
    }, [])
    const onPzSelect = React.useCallback(
      (pz: SinglePersonalizationType) => {
        if (pz.disabled) {
          return
        }
        onSelect(pz)
        onMenuClose()
      },
      [onMenuClose, onSelect],
    )

    const pzGroups = React.useMemo(() => {
      const normalizedSearch = search.trim().toLowerCase()

      return (Object.keys(personalizationList) as PzGroup[])
        .map((key) => {
          const items = personalizationList[key] ?? []
          return {
            items: normalizedSearch
              ? items.filter((item) => item.label.toLowerCase().includes(normalizedSearch))
              : items,
            key,
          }
        })
        .filter(({ items }) => items.length > 0)
    }, [personalizationList, search])
    const onSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearch(event.target.value)
    }, [])
    const onSearchKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
          return
        }

        event.stopPropagation()
        if (event.key !== 'Enter') {
          return
        }

        event.preventDefault()
        const firstEnabledPz = pzGroups.flatMap(({ items }) => items).find((pz) => !pz.disabled)
        if (firstEnabledPz) {
          onPzSelect(firstEnabledPz)
        }
      },
      [pzGroups, onPzSelect],
    )
    return (
      <>
        <Container sx={containerSx}>
          <div
            ref={anchorEl}
            onClick={onMenuOpen}
            onMouseDown={(event) => {
              // Keep blur validation from moving the trigger before the click completes.
              if (event.button === 0) event.preventDefault()
            }}
          >
            <CustomTooltip title={isEmpty(personalizationList) ? 'Personalization not available' : 'Personalization'}>
              <div>
                {variant && (
                  <PZMenuButton
                    color={color}
                    data-testid={testId?.button || `${id}-pz-menu-toggle`}
                    id={`${id}-pz-menu-toggle`}
                    variant={variant}
                    disabled={disabled || isEmpty(personalizationList)}
                  >
                    <Icon>code</Icon>
                  </PZMenuButton>
                )}
                {!variant && (
                  <PZMenuIconButton
                    color={color}
                    data-testid={testId?.button || `${id}-pz-menu-toggle`}
                    id={`${id}-pz-menu-toggle`}
                    disabled={disabled || isEmpty(personalizationList)}
                  >
                    <Icon>code</Icon>
                  </PZMenuIconButton>
                )}
              </div>
            </CustomTooltip>
          </div>
        </Container>
        <Menu
          autoFocus={false}
          data-testid={testId?.menu || `${id}-pz-menu`}
          id={`${id}-pz-menu`}
          open={pzOpen}
          onClose={onMenuClose}
          anchorEl={anchorEl.current}
          disableAutoFocusItem={true}
          slotProps={{
            paper: {
              sx: {
                maxHeight: 'min(400px, calc(100vh - 32px))',
                maxWidth: 'calc(100vw - 32px)',
                width: 320,
              },
            },
            transition: {
              onEntered: onMenuEntered,
            },
          }}
        >
          <MenuList>
            <ListSubheader disableSticky role="presentation" sx={{ py: 1 }}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                label="Search personalizations"
                size="small"
                value={search}
                onChange={onSearchChange}
                onKeyDown={onSearchKeyDown}
              />
            </ListSubheader>
            {pzGroups.length === 0 && (
              <ListSubheader disableSticky role="status">
                No personalizations found
              </ListSubheader>
            )}
            {pzGroups.map(({ items, key }, index) => (
              <React.Fragment key={key}>
                <ListSubheader aria-level={3} disableSticky role="heading">
                  {getGroupTitle(key)}
                </ListSubheader>
                {items.map((pz) => {
                  return (
                    <MenuItem
                      id={`pz_${pz.value.split('.').join('_')}`}
                      key={pz.value}
                      disabled={pz.disabled}
                      tooltip={pz.disabled ? [pz.label, pz.disabledReason].filter(Boolean).join(' — ') : undefined}
                      onClick={() => onPzSelect(pz)}
                    >
                      <CustomTooltip title={pz.disabled ? '' : pz.label}>
                        <PZMenuLabel>{pz.label}</PZMenuLabel>
                      </CustomTooltip>
                    </MenuItem>
                  )
                })}
                {index + 1 < pzGroups.length && <Divider />}
              </React.Fragment>
            ))}
          </MenuList>
        </Menu>
      </>
    )
  },
)

const Container = styled('div')(() => ({
  width: 40,
}))

const PZMenuLabel = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const PZMenuIconButton = styled(IconButton)(() => ({
  borderRadius: '50%',
  height: 40,
  width: 40,
  minWidth: 40,
}))

const PZMenuButton = styled(Button)(() => ({
  borderRadius: '50%',
  height: 40,
  width: 40,
  minWidth: 40,
}))

export default PzMenu
